import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { HiddenDefaultMessageTemplateKey } from '../../../models/MessageTemplates';
import { ITemplateCard, KnownCategories } from '../../../viewmodels/AppViewModels';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/Select';
import { OopsNotFoundGraphic } from '../../components/svgs/graphics/OopsNotFoundGraphic';
import { SearchIcon } from '../../components/svgs/icons/SearchIcon';
import { CampaignTemplateCard } from '../../components/templateCards/CampaignTemplateCard';
import { TextInput } from '../../components/TextInput';
import { HtmlNewslettersCampaignCards } from './HtmlNewslettersCampaignCards';
import { styleSheet } from './styles';

export const SearchKeywords: React.FC<{
	onChange: (value: string) => void;
	value?: string;
	disabled?: boolean;
}> = ({ onChange, value, disabled }) => {
	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		onChange(event.target.value);
	};
	return (
		<div className={css(styleSheet.searchKeywords)}>
			<TextInput
				className={css(styleSheet.searchBox)}
				inputClassName={css(styleSheet.searchBoxInput)}
				inputId='campaign-browser-search'
				leftAccessory={
					<span className={css(styleSheet.searchAccessory)}>
						<SearchIcon />
					</span>
				}
				onChange={handleSearchChange}
				placeholder='Search by keywords'
				type='text'
				value={value}
				disabled={disabled}
			/>
		</div>
	);
};

export const templateScopeFilterOptions = [
	{
		value: Api.TemplateScopeFilter.All,
		label: 'All Templates',
	},
	{
		value: Api.TemplateScopeFilter.UserOnly,
		label: 'My Templates Only',
	},
	{
		value: Api.TemplateScopeFilter.AccountOnly,
		label: 'Shared Templates Only',
	},
] as const;

export const emailCampaignSortByOptions = [
	{
		value: 'creationDate_desc',
		label: 'Creation Date (Newest First)',
	},
	{
		value: 'creationDate_asc',
		label: 'Creation Date (Oldest First)',
	},
	{
		value: 'name_asc',
		label: 'Template Name (A-Z)',
	},
	{
		value: 'name_desc',
		label: 'Template Name (Z-A)',
	},
] as const;

export const SortBySelector: React.FC<{
	onChange: (value: string) => void;
	selectedValue: string;
}> = ({ onChange, selectedValue }) => {
	return (
		<div className={css(styleSheet.sortBySelector)}>
			<div>Sort by:</div>
			<Select items={emailCampaignSortByOptions} value={selectedValue} onValueChange={onChange}>
				<SelectTrigger styles={[styleSheet.sortBySelect]}>
					<SelectValue placeholder='Select...' />
				</SelectTrigger>
				<SelectContent>
					{emailCampaignSortByOptions.map(option => (
						<SelectItem key={option.value} value={option.value}>
							{option.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	);
};

export const renderLoading = () => {
	return (
		<div className={css(styleSheet.loadingContainer)}>
			<LoadingSpinner type='large' />
		</div>
	);
};

export const renderNotFound = (key?: string) => {
	return (
		<div key={key} className={css(styleSheet.noCampaigns)}>
			<figure>
				<OopsNotFoundGraphic />
			</figure>
			<p>We did not find any campaigns based on your search.</p>
			<p>Please try a different search.</p>
		</div>
	);
};

export const HTMLNewsletter = ({
	search,
	category,
}: {
	search: string;
	category: KnownCategories;
	industry: Api.Industry;
}) => {
	const userSession = useUserSession();
	if (!search && category === KnownCategories.HtmlNewsletters) {
		if (!userSession?.account?.features?.htmlNewsletter?.enabled) {
			return (
				<div className={css(styleSheet.htmlEmpty)}>
					<p className={css(styleSheet.htmlEmptyText)}>HTML Newsletters are disabled for this account.</p>
					<p className={css(styleSheet.htmlEmptyText)}>Contact your account Admin to enable.</p>
				</div>
			);
		}

		return <HtmlNewslettersCampaignCards />;
	}
	return null;
};

export const rowizeCards = (cards: ITemplateCard[]): ITemplateCard[][] => {
	if (!cards?.length) {
		return [];
	}
	const hideDefaultMessages = localStorage.getItem(HiddenDefaultMessageTemplateKey);
	const copy = JSON.parse(JSON.stringify(cards.filter(x => !hideDefaultMessages?.includes(x.id))));
	const rows: ITemplateCard[][] = new Array(Math.ceil(copy.length / 3)).fill(undefined)?.map(() => copy.splice(0, 3));
	return rows;
};

export const SectionCards = ({
	category,
	title,
	templates,
	industry,
	onViewCampaign,
	onDeleteClick,
	onShareClick,
	areAccountTemplates,
	header,
}: {
	category?: KnownCategories;
	title: string;
	templates?: ITemplateCard[];
	industry: Api.Industry;
	onViewCampaign: (templateId: string, socialIndicator?: boolean) => void;
	onDeleteClick: (templateCard: ITemplateCard) => void;
	onShareClick: (templateCard: ITemplateCard) => (isShared: boolean) => void;
	areAccountTemplates: boolean;
	header?: React.ReactNode;
}) => {
	return (
		<ContentLibrarySection>
			{header}
			<div className={css(styleSheet.grid)}>
				{templates.map((template, index) => (
					<CampaignTemplateCard
						category={category}
						card={template}
						industry={industry}
						isAccountTemplate={areAccountTemplates}
						isFeatured={title.includes('Featured')}
						isFinraReviewed={title.includes('FINRA Reviewed')}
						key={template.id}
						onDelete={() => onDeleteClick(template)}
						onShare={onShareClick(template)}
						onSocialClicked={() => onViewCampaign(template.id, true)}
						rowIndex={0}
						templateIndex={index}
					/>
				))}
			</div>
		</ContentLibrarySection>
	);
};

export const ContentLibraryContent = ({ styles = [], ...props }: AphroditeStyled<React.ComponentProps<'div'>>) => {
	return <div className={css(styleSheet.templateSection, ...styles)} {...props} />;
};

export const ContentLibrarySection = ({ styles = [], ...props }: AphroditeStyled<React.ComponentProps<'div'>>) => {
	return <div className={css(styleSheet.cardSection, ...styles)} {...props} />;
};

export const ContentLibrarySectionHeader = ({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<'div'>>) => {
	return <div className={css(styleSheet.cardSectionHeader, ...styles)} {...props} />;
};

export const ContentLibrarySectionHeaderTitle = ({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<'h2'>>) => {
	return <h2 className={css(styleSheet.categoryTitle, ...styles)} {...props} />;
};
